import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MessageSquare, Eye, Search, Trash2, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { student: "طالبة", parent: "ولي أمر", trainer: "مدربة", admin: "إدارة" };

export default function AdminMessages() {
    const [convs, setConvs] = useState([]);
    const [q, setQ] = useState("");
    const [overview, setOverview] = useState({});
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [delTarget, setDelTarget] = useState(null);

    const load = async () => {
        const [c, o] = await Promise.all([
            api.get("/admin/messages/conversations"),
            api.get("/admin/messages/overview"),
        ]);
        setConvs(c.data);
        setOverview(o.data);
    };
    useEffect(() => { load(); }, []);

    const openConv = async (c) => {
        setActive(c);
        await loadMessages(c.id);
    };
    const loadMessages = async (convId) => {
        const { data } = await api.get(`/messages/conversations/${convId}/messages`);
        setMessages(data);
    };
    const deleteMessage = async () => {
        if (!delTarget) return;
        try {
            await api.delete(`/admin/messages/${delTarget.id}`);
            toast.success("تم حذف الرسالة نهائياً");
            setDelTarget(null);
            await loadMessages(active.id);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const filtered = convs.filter((c) =>
        !q.trim() ||
        c.participants.some((p) => (p.full_name || "").toLowerCase().includes(q.toLowerCase()) || (p.username || "").toLowerCase().includes(q.toLowerCase())) ||
        (c.last_message_preview || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl font-bold text-[#0F172A]">مراقبة الرسائل</h1>
                <p className="text-sm text-slate-500 mt-1">جميع المحادثات بين الطالبات وأولياء الأمور والمدربات</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="w-10 h-10 rounded-lg bg-[#FFF1F2] text-[#C8102E] flex items-center justify-center mb-3"><MessageSquare className="w-5 h-5" /></div>
                    <div className="text-3xl font-bold text-[#0F172A] font-mono-num" data-testid="stat-conversations">{overview.conversations ?? "-"}</div>
                    <div className="text-xs text-slate-500 mt-1">إجمالي المحادثات</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#1B2A4E] flex items-center justify-center mb-3"><MessageSquare className="w-5 h-5" /></div>
                    <div className="text-3xl font-bold text-[#0F172A] font-mono-num" data-testid="stat-messages">{overview.messages ?? "-"}</div>
                    <div className="text-xs text-slate-500 mt-1">إجمالي الرسائل</div>
                </div>
            </div>

            <div className="flex items-center gap-2 max-w-xl">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في الأسماء أو محتوى الرسائل..." className="pr-10" data-testid="admin-msg-search" />
                </div>
                <Button variant="outline" onClick={load}>تحديث</Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                            <tr>
                                <th className="px-4 py-3 text-right">المشاركات</th>
                                <th className="px-4 py-3 text-right">آخر رسالة</th>
                                <th className="px-4 py-3 text-right">التاريخ</th>
                                <th className="px-4 py-3 text-right">عدد الرسائل</th>
                                <th className="px-4 py-3 w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-400">لا توجد محادثات</td></tr>}
                            {filtered.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1 items-center">
                                            {c.participants.map((p) => (
                                                <Badge key={p.id} variant="outline" className="text-xs font-normal">
                                                    {p.full_name || p.username}
                                                    <span className="mr-1 text-slate-400">({ROLE_LABEL[p.role] || p.role})</span>
                                                </Badge>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600 max-w-sm truncate">{c.last_message_preview || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-slate-500 font-mono-num" dir="ltr">
                                        {c.last_message_at ? new Date(c.last_message_at).toLocaleString("ar-EG") : "—"}
                                    </td>
                                    <td className="px-4 py-3 font-mono-num">{c.message_count}</td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => openConv(c)} data-testid={`view-conv-${c.id}`} className="p-2 rounded hover:bg-slate-100 text-[#1B2A4E]">
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent dir="rtl" className="max-w-2xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-[#C8102E]" />
                            <span>عرض المحادثة</span>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-1">
                        {active?.participants.map((p) => (
                            <Badge key={p.id} variant="outline">
                                {p.full_name} ({ROLE_LABEL[p.role]})
                            </Badge>
                        ))}
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 p-3 bg-slate-50 rounded-lg mt-2">
                        {messages.length === 0 && <div className="text-center text-slate-400 text-sm">لا توجد رسائل</div>}
                        {messages.map((m) => {
                            const sender = active?.participants.find((p) => p.id === m.sender_id);
                            return (
                                <div key={m.id} className={`bg-white border border-slate-200 rounded-lg p-3 group ${m.deleted_for_users ? "opacity-70" : ""}`}>
                                    <div className="flex items-center justify-between mb-1 gap-2">
                                        <div className="text-xs font-semibold text-[#1B2A4E]">
                                            {sender?.full_name}
                                            <span className="text-slate-400 font-normal"> ({ROLE_LABEL[sender?.role]})</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {m.deleted_for_users && (
                                                <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px] gap-1">
                                                    <EyeOff className="w-3 h-3" />
                                                    محذوفة من المستخدمين
                                                </Badge>
                                            )}
                                            <div className="text-[10px] text-slate-400 font-mono-num" dir="ltr">
                                                {new Date(m.created_at).toLocaleString("ar-EG")}
                                            </div>
                                            <button
                                                onClick={() => setDelTarget(m)}
                                                data-testid={`admin-del-msg-${m.id}`}
                                                title="حذف الرسالة نهائياً"
                                                aria-label="حذف نهائي"
                                                className="p-1.5 rounded text-slate-400 hover:text-[#C8102E] hover:bg-[#FFF1F2] transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-sm text-[#0F172A] whitespace-pre-wrap break-words">{m.content}</div>
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>حذف الرسالة نهائياً</AlertDialogTitle>
                        <AlertDialogDescription>
                            سيتم حذف الرسالة بشكل دائم من قاعدة البيانات ولن يمكن استرجاعها.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {delTarget && (
                        <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-[#0F172A] border border-slate-200 whitespace-pre-wrap break-words">
                            {delTarget.content}
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-cancel-admin-del">إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteMessage} data-testid="btn-confirm-admin-del" className="bg-[#C8102E] hover:bg-[#9F1239]">حذف نهائي</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
